import type { FC } from 'react';

/* eslint-disable react/jsx-props-no-spreading */
import classnames from 'classnames';
import { Link, useLocation } from 'react-router-dom';

import { useAppSelector } from '../../../common/hooks/useThunkDispatch';
import { featureFlagsSelectors } from '../../../common/selectors/featureFlagsSelectors';
import { ga4NavigationTracking } from '../../../common/tracking/ga4/navigation';
import { isAriaCurrentPage } from '../../../common/utils/accessibilityUtils';

export interface SiteNavigationItem {
    isActive: boolean;
    name: string;
    url: string;
    contentLinkId: number;
}

interface FeaturedNavigationProps {
    items: SiteNavigationItem[];
    indicatorPosition: 'top' | 'bottom';
}

const FeaturedNavigation: FC<React.PropsWithChildren<FeaturedNavigationProps>> = (props) => {
    const { pathname } = useLocation();

    const fullSpaEnabled = useAppSelector(featureFlagsSelectors.selectFullSpaModeEnabled);

    const getLinkItemProps = (item: SiteNavigationItem) => ({
        className: 'Navigation-Link',
        'data-test': `mainnav-${item.name.toLowerCase().replace(' & ', '-')}`,
        onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
            ga4NavigationTracking.headerNavigationClick(item.name, e.currentTarget.href);
        },
    });

    return (
        <nav aria-label="Genvägar" className="Navigation">
            <ul className="Navigation-List">
                {props.items.map((item) => {
                    const ariaCurrentPage = item.isActive && isAriaCurrentPage(item.url, pathname);
                    return (
                        <li
                            key={item.name}
                            className={classnames(
                                props.indicatorPosition === 'top' && 'Indicator--Top',
                                props.indicatorPosition === 'bottom' && 'Indicator--Bottom',
                                'Navigation-ListItem',
                                item.isActive && 'is-active',
                            )}
                        >
                            {fullSpaEnabled ? (
                                <Link
                                    aria-current={ariaCurrentPage}
                                    to={item.url}
                                    key={item.name}
                                    {...getLinkItemProps(item)}
                                >
                                    <span className="Navigation-LinkText">{item.name}</span>
                                </Link>
                            ) : (
                                <a
                                    aria-current={ariaCurrentPage}
                                    href={item.url}
                                    key={item.name}
                                    {...getLinkItemProps(item)}
                                >
                                    <span className="Navigation-LinkText">{item.name}</span>
                                </a>
                            )}
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};

export default FeaturedNavigation;
